"use client";

import { Archive } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export interface ArchiveSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  ariaLabel?: string;
  icon?: LucideIcon;
}

export function ArchiveSwitch({
  checked,
  onChange,
  disabled = false,
  label = "Archived",
  ariaLabel,
  icon: Icon = Archive,
}: ArchiveSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel ?? label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "inline-flex items-center gap-2 cursor-pointer rounded-lg",
        "text-sm font-medium",
        checked ? "text-gray-900" : "text-gray-500",
        "transition-colors duration-200",
        "motion-safe:active:scale-[0.97]",
        "focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navigator-primary",
        "disabled:opacity-50 disabled:cursor-not-allowed",
      )}
    >
      <span>{label}</span>
      <div
        className={cn(
          "relative w-[60px] h-8 rounded-lg transition-colors duration-200",
          checked ? "bg-navigator-primary" : "bg-gray-200",
        )}
      >
        <div
          className={cn(
            "absolute top-0.5 flex items-center justify-center w-7 h-7 rounded-md bg-white shadow-sm transition-transform duration-200",
            checked ? "translate-x-[30px]" : "translate-x-0.5",
          )}
        >
          <Icon
            className={cn(
              "w-3.5 h-3.5 transition-colors duration-200",
              checked ? "text-navigator-primary" : "text-gray-400",
            )}
          />
        </div>
      </div>
    </button>
  );
}
